import React, { useState, useRef, useEffect } from 'react';
import {
  Package,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Barcode,
  Clock,
  Activity,
  ArrowUp,
  ArrowDown,
  Plus,
  Minus,
  X,
  PlusCircle,
  ShoppingBag,
  Loader,
  Users,
  Receipt,
  CreditCard,
  Lightbulb,
  TrendingUp,
  PercentIcon,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Lock,
  Shield,
  Smartphone,
  Image as ImageIcon,
  ZoomIn,
  Download
} from 'lucide-react';

import { authFetch } from "../utils/authFetch";

// Configuración de la API
const API_URL = process.env.REACT_APP_API_URL
  || (window.location.hostname === 'localhost'
    ? 'http://localhost:3001/api'
    : 'https://alvarezbohlback-production.up.railway.app/api');

const PointOfSale = () => {
  const [activeSubView, setActiveSubView] = useState("inventory");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [currentOrder, setCurrentOrder] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [showOrderReceipt, setShowOrderReceipt] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [scannedBarcode, setScannedBarcode] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scanQuantity, setScanQuantity] = useState(1);
  const [isSyncing, setIsSyncing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [notifications, setNotifications] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardName, setCardName] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState("method");
  const barcodeInputRef = useRef(null);

  // NUEVOS: Estados para modal de imagen
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Estados para datos del backend
  const [storeProducts, setStoreProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [storeSales, setStoreSales] = useState([]);
  const [orders, setOrders] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // NUEVAS: Funciones para modal de imagen
  const handleImageClick = (imageUrl, productName) => {
    setSelectedImage({
      url: imageUrl,
      description: productName
    });
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleDownloadImage = () => {
    if (!selectedImage?.url) return;
    
    const link = document.createElement('a');
    link.href = selectedImage.url;
    link.download = `producto-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadProducts(),
        loadClients(),
        loadSales(),
        loadOrders(),
        loadRecommendations()
      ]);
      setLoading(false);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Error al cargar los datos');
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await authFetch(`${API_URL}/products`);
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        const transformedProducts = result.data.map(product => ({
          id: product.id,
          name: product.descripcion || 'Sin descripción',
          barcode: product.barcode,
          price: product.price || 0,
          stock: product.stock || 0,
          min_stock: product.min_stock || 0,
          category: product.categorias?.nombre || 'Sin categoría',
          proveedor_id: product.proveedor_id,
          proveedor_nombre: product.proveedores?.nombre || 'Sin proveedor',
          familia_nombre: product.familias?.nombre || 'Sin familia',
          imagen_url: product.imagen_url || null,
          _original: product
        }));
        setStoreProducts(transformedProducts);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadClients = async () => {
    try {
      const response = await authFetch(`${API_URL}/clients`);
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setClients(result.data);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const loadSales = async () => {
    try {
      const response = await authFetch(`${API_URL}/sales`);
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        const salesWithDates = result.data.map(sale => ({
          ...sale,
          timestamp: new Date(sale.timestamp)
        }));
        setStoreSales(salesWithDates);
        
        const pendingCount = salesWithDates.filter(sale => !sale.synced).length;
        setNotifications(pendingCount);
      }
    } catch (err) {
      console.error('Error loading sales:', err);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await authFetch(`${API_URL}/orders`);
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        const ordersWithDates = result.data.map(order => ({
          ...order,
          timestamp: new Date(order.timestamp),
          products: order.order_items || []
        }));
        setOrders(ordersWithDates);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  const loadRecommendations = async () => {
    try {
      const response = await authFetch(`${API_URL}/recommendations`);
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        const recsWithDates = result.data.map(rec => ({
          ...rec,
          timestamp: new Date(rec.timestamp)
        }));
        setRecommendations(recsWithDates);
      }
    } catch (err) {
      console.error('Error loading recommendations:', err);
    }
  };

  const categories = ["Todos", ...new Set(storeProducts.map(product => product.category))];

  const filteredProducts = selectedCategory === "Todos" 
    ? storeProducts 
    : storeProducts.filter(product => product.category === selectedCategory);

  // eslint-disable-next-line no-unused-vars
  const stats = {
    total: storeSales.length,
    good: storeSales.filter(sale => sale.synced).length,
    efficiency: Math.round((storeSales.filter(sale => sale.synced).length / storeSales.length) * 100) || 100,
  };

  const startBarcodeScanning = () => {
    setShowBarcodeScanner(true);
    setIsScanning(false);
    setScannedBarcode("");
    setScanQuantity(1);
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
  };

  const simulateBarcodeScanning = () => {
    setIsScanning(true);
    setTimeout(() => {
      const randomProduct = storeProducts[Math.floor(Math.random() * storeProducts.length)];
      setScannedBarcode(randomProduct.barcode);
      setIsScanning(false);
    }, 1500);
  };

  const handleBarcodeInput = async (e) => {
    if (e.key === 'Enter') {
      try {
        const response = await authFetch(`${API_URL}/products/search/barcode/${e.target.value}`);
        if (!response) return;
        const result = await response.json();
        
        if (result.success && result.data) {
          setScannedBarcode(result.data.barcode);
        } else {
          alert("Producto no encontrado");
          setScannedBarcode("");
        }
      } catch (err) {
        console.error('Error fetching product by barcode:', err);
        alert("Error al buscar el producto");
      }
    }
  };

  const addScannedProductToOrder = () => {
    const product = storeProducts.find(p => p.barcode === scannedBarcode);
    if (!product) return;
    
    if (product.stock < scanQuantity) {
      alert(`Stock insuficiente. Solo hay ${product.stock} unidades disponibles.`);
      return;
    }
    
    const existingItem = currentOrder.find(item => item.product_id === product.id);
    
    if (existingItem) {
      setCurrentOrder(prev => prev.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + scanQuantity } 
          : item
      ));
    } else {
      setCurrentOrder(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity: scanQuantity,
        unit_price: parseFloat(product.price),
        barcode: product.barcode,
        imagen_url: product.imagen_url
      }]);
    }
    
    setScannedBarcode("");
    setScanQuantity(1);
    
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  const processCardPayment = async () => {
    if (paymentMethod === 'card' && (!cardNumber || !expiryDate || !cvv || !cardName)) {
      alert("Por favor complete todos los campos de la tarjeta");
      return;
    }
    
    setIsProcessingPayment(true);
    setPaymentStep("processing");
    
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const saleData = {
        client_id: selectedClient ? parseInt(selectedClient) : null,
        client_name: selectedClient 
          ? clients.find(c => c.id === parseInt(selectedClient))?.name || "Cliente sin registro"
          : "Cliente sin registro",
        items: currentOrder,
        payment_method: paymentMethod,
        card_last4: paymentMethod === 'card' ? cardNumber.slice(-4) : null
      };

      const response = await authFetch(`${API_URL}/sales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saleData)
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        setPaymentStep("success");
        
        await Promise.all([
          loadProducts(),
          loadSales(),
          loadClients()
        ]);
        
        setTimeout(() => {
          setShowPaymentModal(false);
          setCurrentOrder([]);
          setShowBarcodeScanner(false);
          setSelectedClient("");
          setPaymentStep("method");
          setCardNumber("");
          setExpiryDate("");
          setCvv("");
          setCardName("");
        }, 2000);
      } else {
        alert('Error al procesar la venta: ' + result.error);
        setPaymentStep("method");
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      alert('Error al procesar el pago');
      setPaymentStep("method");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const syncWithCentral = async () => {
    try {
      setIsSyncing(true);
      
      const response = await authFetch(`${API_URL}/sales/sync/all`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        await loadSales();
        setNotifications(0);
      } else {
        alert('Error al sincronizar: ' + result.error);
      }
    } catch (err) {
      console.error('Error syncing:', err);
      alert('Error al sincronizar con el sistema central');
    } finally {
      setIsSyncing(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('¿Está seguro de eliminar este cliente?')) return;

    try {
      const response = await authFetch(`${API_URL}/clients/${clientId}`, {
        method: 'DELETE'
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        await loadClients();
        alert('Cliente eliminado correctamente');
      } else {
        alert('Error al eliminar cliente: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      alert('Error al eliminar el cliente');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await authFetch(`${API_URL}/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        await Promise.all([loadOrders(), loadProducts()]);
        alert('Estado del pedido actualizado');
      } else {
        alert('Error al actualizar: ' + result.error);
      }
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Error al actualizar el pedido');
    }
  };

  const updateProductStock = async (productId, operation) => {
    try {
      const response = await authFetch(`${API_URL}/products/${productId}/stock`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operation, amount: 1 })
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        await loadProducts();
      } else {
        alert('Error al actualizar stock: ' + result.error);
      }
    } catch (err) {
      console.error('Error updating stock:', err);
      alert('Error al actualizar el stock');
    }
  };

  const orderTotal = currentOrder.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const getRecommendationIcon = (type) => {
    switch (type) {
      case "restock": return Package;
      case "trending": return TrendingUp;
      case "seasonal": return Calendar;
      case "pricing": return PercentIcon;
      default: return Lightbulb;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "#EF4444";
      case "medium": return "#F59E0B";
      case "low": return "#3B82F6";
      default: return "#6B7280";
    }
  };

  const subMenuItems = [
    { id: "inventory", label: "Inventario", icon: Package },
    { id: "clients", label: "Clientes", icon: Users },
    { id: "orders", label: "Pedidos", icon: ShoppingBag },
    { id: "sales", label: "Ventas", icon: Receipt },
  ];

  const renderSubView = () => {
    switch (activeSubView) {
      case "inventory":
        return renderInventoryView();
      case "clients":
        return renderClientsView();
      case "orders":
        return renderOrdersView();
      case "sales":
        return renderSalesView();
      default:
        return renderInventoryView();
    }
  };

  const renderInventoryView = () => (
    <div className="inventory-view">
      <div className="inventory-grid">
        {/* Panel de Productos CON IMÁGENES CLICKEABLES */}
        <div className="panel full-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Package className="panel-icon" />
            </div>
            <h3>Inventario de Productos</h3>
            <div className="category-filter">
              {categories.map(category => (
                <button
                  key={category}
                  className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          <div className="products-list">
            {filteredProducts.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-info">
                  {/* IMAGEN CLICKEABLE DEL PRODUCTO */}
                  <div className="product-image">
                    {product.imagen_url ? (
                      <div 
                        className="product-img-container"
                        onClick={() => handleImageClick(product.imagen_url, product.name)}
                      >
                        <img 
                          src={product.imagen_url} 
                          alt={product.name}
                          className="product-img"
                        />
                        <div className="image-overlay">
                          <ZoomIn size={20} />
                        </div>
                      </div>
                    ) : (
                      <div className="product-img-placeholder">
                        <ImageIcon size={24} />
                      </div>
                    )}
                  </div>
                  <div className="product-details">
                    <h4>{product.name}</h4>
                    <p className="category">{product.category}</p>
                    <div className="price">${parseFloat(product.price).toFixed(2)}</div>
                    <div className="barcode">
                      <Barcode size={12} />
                      <span>{product.barcode}</span>
                    </div>
                  </div>
                  <div className={`stock-indicator ${product.stock <= product.min_stock ? "low" : "normal"}`}>
                    <span className="stock-number">{product.stock}</span>
                    <span className="stock-label">en stock</span>
                    {product.stock <= product.min_stock && (
                      <AlertCircle className="warning-icon" />
                    )}
                  </div>
                </div>
                <div className="product-actions">
                  <div className="quantity-controls">
                    <button 
                      className="qty-btn"
                      onClick={() => updateProductStock(product.id, 'subtract')}
                      disabled={product.stock <= 0}
                    >
                      <Minus size={14} />
                    </button>
                    <button 
                      className="qty-btn"
                      onClick={() => updateProductStock(product.id, 'add')}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recomendaciones IA */}
        <div className="panel half-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Lightbulb className="panel-icon" />
            </div>
            <h3>Recomendaciones IA</h3>
          </div>
          <div className="recommendations-list">
            {recommendations.map((rec) => {
              const IconComponent = getRecommendationIcon(rec.type);
              return (
                <div key={rec.id} className={`recommendation-item priority-${rec.priority}`}>
                  <div className="rec-icon-wrapper" style={{ borderColor: getPriorityColor(rec.priority) }}>
                    <IconComponent className="rec-icon" style={{ color: getPriorityColor(rec.priority) }} />
                  </div>
                  <div className="rec-content">
                    <div className="rec-header">
                      <span className="rec-product">{rec.product_name}</span>
                      <span className={`rec-priority priority-${rec.priority}`}>{rec.priority}</span>
                    </div>
                    <p className="rec-message">{rec.message}</p>
                    <div className="rec-timestamp">
                      {rec.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ventas Recientes */}
        <div className="panel half-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Activity className="panel-icon" />
            </div>
            <h3>Ventas Recientes</h3>
            <div className="sync-status">
              {storeSales.filter(sale => !sale.synced).length > 0 && (
                <span className="unsync-badge">
                  {storeSales.filter(sale => !sale.synced).length} sin sincronizar
                </span>
              )}
            </div>
          </div>
          <div className="sales-list">
            {storeSales.slice(-10).reverse().map((sale) => (
              <div key={sale.id} className={`sale-item ${sale.synced ? "synced" : "pending"}`}>
                <div className="sale-info">
                  <div className="sale-product">{sale.product_name}</div>
                  <div className="sale-details">
                    <span className="quantity">Qty: {sale.quantity}</span>
                    <span className="amount">${parseFloat(sale.total).toFixed(2)}</span>
                    <span className="time">{sale.timestamp.toLocaleTimeString()}</span>
                  </div>
                </div>
                <div className={`sync-indicator ${sale.synced ? "synced" : "pending"}`}>
                  {sale.synced ? <CheckCircle size={16} /> : <Clock size={16} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderClientsView = () => (
    <div className="clients-view">
      <div className="view-header">
        <h3>Gestión de Clientes</h3>
        <button className="add-button">
          <Plus size={16} />
          <span>Añadir Cliente</span>
        </button>
      </div>
      <div className="clients-table">
        <div className="table-header">
          <span>Nombre</span>
          <span>Email</span>
          <span>Teléfono</span>
          <span>Compras Totales</span>
          <span>Acciones</span>
        </div>
        {clients.map(client => (
          <div key={client.id} className="table-row">
            <span>{client.name}</span>
            <span>{client.email}</span>
            <span>{client.phone}</span>
            <span>${parseFloat(client.total_purchases).toFixed(2)}</span>
            <div className="action-buttons">
              <button className="action-btn view">
                <Eye size={14} />
              </button>
              <button className="action-btn edit">
                <Edit size={14} />
              </button>
              <button className="action-btn delete" onClick={() => deleteClient(client.id)}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderOrdersView = () => (
    <div className="orders-view">
      <div className="view-header">
        <h3>Pedidos al Sistema Central</h3>
        <button className="add-button">
          <Plus size={16} />
          <span>Nuevo Pedido</span>
        </button>
      </div>
      <div className="orders-table">
        <div className="table-header">
          <span>ID Pedido</span>
          <span>Productos</span>
          <span>Estado</span>
          <span>Fecha</span>
          <span>Total</span>
          <span>Acciones</span>
        </div>
        {orders.map(order => (
          <div key={order.id} className="table-row">
            <span>#{order.id.toString().padStart(4, '0')}</span>
            <span>{order.products.map(p => p.product_name).join(", ")}</span>
            <span className={`status-badge ${order.status}`}>
              {order.status === 'pending' ? 'Pendiente' : 'Aprobado'}
            </span>
            <span>{order.timestamp.toLocaleDateString()}</span>
            <span>${parseFloat(order.total).toFixed(2)}</span>
            <div className="action-buttons">
              <button className="action-btn view">
                <Eye size={14} />
              </button>
              <button className="action-btn edit" onClick={() => updateOrderStatus(order.id, order.status === 'pending' ? 'approved' : 'pending')}>
                <Edit size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSalesView = () => (
    <div className="sales-view">
      <div className="view-header">
        <h3>Registro de Ventas</h3>
        <button className="add-button">
          <Plus size={16} />
          <span>Nueva Venta</span>
        </button>
      </div>
      <div className="sales-table">
        <div className="table-header">
          <span>ID Venta</span>
          <span>Cliente</span>
          <span>Productos</span>
          <span>Cantidad</span>
          <span>Total</span>
          <span>Fecha</span>
          <span>Estado</span>
        </div>
        {storeSales.map(sale => (
          <div key={sale.id} className="table-row">
            <span>#{sale.id.toString().padStart(4, '0')}</span>
            <span>{sale.client_name}</span>
            <span>{sale.product_name}</span>
            <span>{sale.quantity}</span>
            <span>${parseFloat(sale.total).toFixed(2)}</span>
            <span>{sale.timestamp.toLocaleDateString()}</span>
            <span className={`sync-status ${sale.synced ? 'synced' : 'pending'}`}>
              {sale.synced ? 'Sincronizado' : 'Pendiente'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="point-of-sale loading-container">
        <Loader className="spinning-large" />
        <p>Cargando datos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="point-of-sale error-container">
        <AlertCircle size={48} color="#EF4444" />
        <p>{error}</p>
        <button onClick={loadInitialData}>Reintentar</button>
      </div>
    );
  }

  return (
    <div className="point-of-sale">
      <header className="content-header">
        <div className="header-info">
          <h2>Point of Sale</h2>
          <p>Gestión de inventario y ventas en tiempo real</p>
        </div>
        <div className="header-actions">
          <button onClick={startBarcodeScanning} className="scan-button">
            <Barcode className="scan-icon" />
            <span>Escanear Productos</span>
          </button>
          <button 
            onClick={syncWithCentral}
            disabled={isSyncing}
            className="sync-button"
          >
            <RefreshCw className={`sync-icon ${isSyncing ? "spinning" : ""}`} />
            <span>{isSyncing ? "Sincronizando..." : "Sincronizar"}</span>
          </button>
          <div className="header-metrics">
            <div className="metric-item">
              <div className="metric-icon good">
                <CheckCircle />
              </div>
              <div>
                <strong>{storeProducts.reduce((sum, p) => sum + p.stock, 0)}</strong>
                <span>Items en Stock</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="daily-summary-compact">
        <div className="daily-summary-grid-compact">
          <div className="daily-metric-compact">
            <div className="daily-metric-value">{storeSales.length}</div>
            <div className="daily-metric-label">Transacciones</div>
            <div className="daily-metric-trend positive">
              <ArrowUp size={12} />
              <span>12%</span>
            </div>
          </div>
          <div className="daily-metric-compact">
            <div className="daily-metric-value">
              ${storeSales.reduce((sum, sale) => sum + parseFloat(sale.total), 0).toFixed(2)}
            </div>
            <div className="daily-metric-label">Ingresos Totales</div>
            <div className="daily-metric-trend positive">
              <ArrowUp size={12} />
              <span>8.5%</span>
            </div>
          </div>
          <div className="daily-metric-compact">
            <div className="daily-metric-value">
              ${storeSales.length > 0 ? (storeSales.reduce((sum, sale) => sum + parseFloat(sale.total), 0) / storeSales.length).toFixed(2) : '0.00'}
            </div>
            <div className="daily-metric-label">Ticket Promedio</div>
            <div className="daily-metric-trend negative">
              <ArrowDown size={12} />
              <span>2.3%</span>
            </div>
          </div>
          <div className="daily-metric-compact">
            <div className="daily-metric-value">
              {storeSales.reduce((sum, sale) => sum + sale.quantity, 0)}
            </div>
            <div className="daily-metric-label">Unidades Vendidas</div>
            <div className="daily-metric-trend positive">
              <ArrowUp size={12} />
              <span>15%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="sub-navigation">
        {subMenuItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveSubView(item.id)}
            className={`sub-nav-item ${activeSubView === item.id ? 'active' : ''}`}
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      <div className="content-body">
        {!showBarcodeScanner && !showOrderReceipt && renderSubView()}

        {/* Vista de Escáner CON IMÁGENES CLICKEABLES */}
        {showBarcodeScanner && (
          <div className="barcode-scanner-view">
            <div className="panel full-width">
              <div className="panel-header">
                <div className="panel-icon-wrapper">
                  <Barcode className="panel-icon" />
                </div>
                <h3>Escáner de Código de Barras</h3>
                <button className="close-button" onClick={() => setShowBarcodeScanner(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className="scanner-content">
                <div className="scanner-input-area">
                  <div className="scanner-field">
                    <label>Código de Barras</label>
                    <div className="barcode-input-container">
                      <input 
                        type="text" 
                        value={scannedBarcode} 
                        onChange={(e) => setScannedBarcode(e.target.value)}
                        onKeyDown={handleBarcodeInput}
                        placeholder="Escanee un producto o ingrese código manualmente"
                        disabled={isScanning}
                        ref={barcodeInputRef}
                      />
                      <button 
                        className="scan-simulate-button"
                        onClick={simulateBarcodeScanning}
                        disabled={isScanning}
                      >
                        {isScanning ? <Loader className="spinning" /> : <Barcode size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  {/* PRODUCTO ESCANEADO CON IMAGEN CLICKEABLE */}
                  {scannedBarcode && (
                    <div className="scanned-product">
                      <div className="scanned-product-image">
                        {storeProducts.find(p => p.barcode === scannedBarcode)?.imagen_url ? (
                          <div 
                            className="scanned-img-container"
                            onClick={() => handleImageClick(
                              storeProducts.find(p => p.barcode === scannedBarcode).imagen_url,
                              storeProducts.find(p => p.barcode === scannedBarcode)?.name
                            )}
                          >
                            <img 
                              src={storeProducts.find(p => p.barcode === scannedBarcode).imagen_url}
                              alt={storeProducts.find(p => p.barcode === scannedBarcode)?.name}
                              className="scanned-img"
                            />
                            <div className="image-overlay">
                              <ZoomIn size={24} />
                            </div>
                          </div>
                        ) : (
                          <div className="scanned-img-placeholder">
                            <ImageIcon size={48} />
                          </div>
                        )}
                      </div>
                      <div className="scanned-product-info">
                        <h4>{storeProducts.find(p => p.barcode === scannedBarcode)?.name || "Producto no encontrado"}</h4>
                        <p className="scanned-price">
                          ${storeProducts.find(p => p.barcode === scannedBarcode)?.price ? parseFloat(storeProducts.find(p => p.barcode === scannedBarcode).price).toFixed(2) : "0.00"}
                        </p>
                      </div>
                      <div className="quantity-selector">
                        <label>Cantidad:</label>
                        <div className="quantity-controls">
                          <button 
                            onClick={() => setScanQuantity(prev => Math.max(1, prev - 1))}
                            className="qty-btn"
                          >
                            <Minus size={14} />
                          </button>
                          <input 
                            type="number" 
                            min="1" 
                            value={scanQuantity} 
                            onChange={(e) => setScanQuantity(parseInt(e.target.value) || 1)}
                          />
                          <button 
                            onClick={() => setScanQuantity(prev => prev + 1)}
                            className="qty-btn"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                      <button className="add-to-order-button" onClick={addScannedProductToOrder}>
                        <PlusCircle size={18} />
                        <span>Añadir al Pedido</span>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* PEDIDO ACTUAL CON IMÁGENES CLICKEABLES */}
                <div className="current-order">
                  <h3>Pedido Actual</h3>
                  {currentOrder.length === 0 ? (
                    <div className="empty-order">
                      <ShoppingBag size={48} />
                      <p>No hay productos en el pedido actual</p>
                      <p>Escanee un producto para comenzar</p>
                    </div>
                  ) : (
                    <>
                      <div className="order-items">
                        {currentOrder.map((item, index) => (
                          <div key={index} className="order-item">
                            <div className="order-item-image">
                              {item.imagen_url ? (
                                <div 
                                  className="order-img-container"
                                  onClick={() => handleImageClick(item.imagen_url, item.product_name)}
                                >
                                  <img 
                                    src={item.imagen_url}
                                    alt={item.product_name}
                                    className="order-img"
                                  />
                                  <div className="image-overlay-small">
                                    <ZoomIn size={16} />
                                  </div>
                                </div>
                              ) : (
                                <div className="order-img-placeholder">
                                  <ImageIcon size={20} />
                                </div>
                              )}
                            </div>
                            <div className="order-item-info">
                              <h4>{item.product_name}</h4>
                              <div className="order-item-details">
                                <span>{item.quantity} x ${item.unit_price.toFixed(2)}</span>
                                <span className="order-item-total">${(item.quantity * item.unit_price).toFixed(2)}</span>
                              </div>
                            </div>
                            <button 
                              className="remove-item-button"
                              onClick={() => setCurrentOrder(prev => prev.filter((_, i) => i !== index))}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="order-summary">
                        <div className="order-total">
                          <h4>Total</h4>
                          <span>${orderTotal.toFixed(2)}</span>
                        </div>
                        <div className="order-actions">
                          <button className="cancel-order-button" onClick={() => {setCurrentOrder([]); setShowBarcodeScanner(false);}}>
                            <X size={16} />
                            <span>Cancelar</span>
                          </button>
                          <button 
                            className="payment-order-button"
                            onClick={() => setShowPaymentModal(true)}
                            disabled={currentOrder.length === 0}
                          >
                            <CreditCard size={16} />
                            <span>Procesar Pago</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL DE VISTA DE IMAGEN */}
        {showImageModal && selectedImage && (
          <div className="image-modal-overlay" onClick={handleCloseImageModal}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="image-modal-header">
                <h3>{selectedImage.description || 'Imagen del Producto'}</h3>
                <div className="image-modal-actions">
                  <button 
                    className="image-modal-btn download-btn"
                    onClick={handleDownloadImage}
                    title="Descargar imagen"
                  >
                    <Download size={18} />
                  </button>
                  <button 
                    className="image-modal-btn close-btn"
                    onClick={handleCloseImageModal}
                    title="Cerrar"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="image-modal-body">
                <img 
                  src={selectedImage.url} 
                  alt={selectedImage.description || 'Producto'}
                  className="modal-image-large"
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal de Pago (sin cambios) */}
        {showPaymentModal && (
          <div className="modal-overlay">
            <div className="modal-container payment-modal">
              <div className="modal-header">
                <h3>Procesar Pago</h3>
                {paymentStep !== "processing" && paymentStep !== "success" && (
                  <button className="modal-close-button" onClick={() => setShowPaymentModal(false)}>
                    <X size={18} />
                  </button>
                )}
              </div>
              <div className="modal-content">
                {paymentStep === "method" && (
                  <>
                    <div className="payment-summary">
                      <h4>Resumen del Pedido</h4>
                      <div className="order-summary-list">
                        {currentOrder.map((item, index) => (
                          <div key={index} className="summary-item">
                            <span>{item.product_name}</span>
                            <span>{item.quantity} x ${item.unit_price.toFixed(2)}</span>
                            <span>${(item.quantity * item.unit_price).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="total-row">
                        <strong>Total: ${orderTotal.toFixed(2)}</strong>
                      </div>
                    </div>
                    
                    <div className="payment-methods">
                      <label>Método de Pago</label>
                      <div className="payment-options">
                        <div 
                          className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}
                          onClick={() => setPaymentMethod('card')}
                        >
                          <CreditCard size={24} />
                          <span>Tarjeta de Crédito/Débito</span>
                          {paymentMethod === 'card' && <CheckCircle size={20} className="selected-icon" />}
                        </div>
                        <div 
                          className={`payment-option ${paymentMethod === 'cash' ? 'selected' : ''}`}
                          onClick={() => setPaymentMethod('cash')}
                        >
                          <Receipt size={24} />
                          <span>Efectivo</span>
                          {paymentMethod === 'cash' && <CheckCircle size={20} className="selected-icon" />}
                        </div>
                        <div 
                          className={`payment-option ${paymentMethod === 'mobile' ? 'selected' : ''}`}
                          onClick={() => setPaymentMethod('mobile')}
                        >
                          <Smartphone size={24} />
                          <span>Pago Móvil</span>
                          {paymentMethod === 'mobile' && <CheckCircle size={20} className="selected-icon" />}
                        </div>
                      </div>
                    </div>
                    
                    <div className="client-selection">
                      <label>Cliente (Opcional)</label>
                      <select 
                        value={selectedClient} 
                        onChange={(e) => setSelectedClient(e.target.value)}
                        className="client-select"
                      >
                        <option value="">Cliente sin registrar</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.name} - {client.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {paymentStep === "card-details" && paymentMethod === 'card' && (
                  <div className="card-payment-form">
                    <div className="card-visual">
                      <div className="card-mockup">
                        <div className="card-chip"></div>
                        <div className="card-number">
                          {cardNumber || "**** **** **** ****"}
                        </div>
                        <div className="card-info">
                          <div className="card-expiry">{expiryDate || "MM/YY"}</div>
                          <div className="card-name">{cardName || "NOMBRE DEL TITULAR"}</div>
                        </div>
                      </div>
                    </div>
                    <div className="card-form">
                      <div className="form-row">
                        <label>Número de Tarjeta</label>
                        <input 
                          type="text" 
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value)}
                          placeholder="1234 5678 9012 3456"
                          maxLength="19"
                        />
                      </div>
                      <div className="form-row-group">
                        <div className="form-row">
                          <label>Fecha de Expiración</label>
                          <input 
                            type="text" 
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            placeholder="MM/YY"
                            maxLength="5"
                          />
                        </div>
                        <div className="form-row">
                          <label>CVV</label>
                          <input 
                            type="password" 
                            value={cvv}
                            onChange={(e) => setCvv(e.target.value)}
                            placeholder="123"
                            maxLength="4"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <label>Nombre del Titular</label>
                        <input 
                          type="text" 
                          value={cardName}
                          onChange={(e) => setCardName(e.target.value)}
                          placeholder="Como aparece en la tarjeta"
                        />
                      </div>
                      <div className="security-info">
                        <Shield size={16} />
                        <span>Conexión segura SSL. Su información está protegida.</span>
                      </div>
                    </div>
                  </div>
                )}

                {paymentStep === "processing" && (
                  <div className="processing-payment">
                    <div className="processing-animation">
                      <Loader className="spinning-large" />
                    </div>
                    <h4>Procesando Pago</h4>
                    <p>Por favor espere mientras procesamos su pago...</p>
                    <div className="processing-steps">
                      <div className="step completed">✓ Verificando datos</div>
                      <div className="step active">⏳ Contactando banco</div>
                      <div className="step">◦ Confirmando transacción</div>
                    </div>
                  </div>
                )}

                {paymentStep === "success" && (
                  <div className="payment-success">
                    <div className="success-animation">
                      <CheckCircle size={64} className="success-icon" />
                    </div>
                    <h4>¡Pago Completado!</h4>
                    <p>Su transacción ha sido procesada exitosamente</p>
                    <div className="transaction-details">
                      <div className="detail-row">
                        <span>Total pagado:</span>
                        <span>${orderTotal.toFixed(2)}</span>
                      </div>
                      {paymentMethod === 'card' && (
                        <div className="detail-row">
                          <span>Tarjeta terminada en:</span>
                          <span>****{cardNumber.slice(-4)}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span>Método de pago:</span>
                        <span>
                          {paymentMethod === 'card' && 'Tarjeta'}
                          {paymentMethod === 'cash' && 'Efectivo'}
                          {paymentMethod === 'mobile' && 'Pago Móvil'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {paymentStep === "method" && (
                <div className="modal-footer">
                  <button className="cancel-button" onClick={() => setShowPaymentModal(false)}>
                    Cancelar
                  </button>
                  <button 
                    className="continue-button" 
                    onClick={() => {
                      if (paymentMethod === 'card') {
                        setPaymentStep("card-details");
                      } else {
                        processCardPayment();
                      }
                    }}
                  >
                    <CreditCard size={16} />
                    <span>Continuar</span>
                  </button>
                </div>
              )}
              {paymentStep === "card-details" && (
                <div className="modal-footer">
                  <button className="back-button" onClick={() => setPaymentStep("method")}>
                    Volver
                  </button>
                  <button className="pay-button" onClick={processCardPayment}>
                    <Lock size={16} />
                    <span>Pagar ${orderTotal.toFixed(2)}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        /* Variables CSS */
        :root {
          --primary-color: #3B82F6;
          --secondary-color: #2563EB;
          --accent-color: #60A5FA;
          --text-dark: #1F2937;
          --text-medium: #6B7280;
          --text-light: #9CA3AF;
          --bg-light: #F9FAFB;
          --bg-white: #FFFFFF;
          --border-color: #E5E7EB;
          --success-color: #10B981;
          --error-color: #EF4444;
          --warning-color: #F59E0B;
        }

        .point-of-sale {
          padding: 24px;
          background: var(--bg-light);
          min-height: 100vh;
        }

        .loading-container, .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 20px;
        }

        .spinning-large {
          width: 48px;
          height: 48px;
          animation: spin 1s linear infinite;
          color: var(--primary-color);
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ESTILOS PARA IMÁGENES CLICKEABLES */
        .product-image {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          width: 64px;
          height: 64px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
          background: var(--bg-light);
          border: 1px solid var(--border-color);
        }

        .product-img-container, .scanned-img-container, .order-img-container {
          position: relative;
          width: 100%;
          height: 100%;
          cursor: pointer;
          overflow: hidden;
        }

        .product-img-container:hover .image-overlay,
        .scanned-img-container:hover .image-overlay,
        .order-img-container:hover .image-overlay-small {
          opacity: 1;
        }

        .product-img, .scanned-img, .order-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .product-img-container:hover .product-img,
        .scanned-img-container:hover .scanned-img,
        .order-img-container:hover .order-img {
          transform: scale(1.1);
        }

        .image-overlay, .image-overlay-small {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          color: white;
        }

        .product-img-placeholder, .scanned-img-placeholder, .order-img-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background: rgba(59, 130, 246, 0.1);
          color: var(--primary-color);
        }

        /* IMAGEN DEL PRODUCTO ESCANEADO */
        .scanned-product-image {
          width: 100%;
          height: 150px;
          border-radius: 8px;
          overflow: hidden;
          background: var(--bg-light);
          border: 2px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
        }

        /* IMAGEN EN ITEM DEL PEDIDO */
        .order-item-image {
          width: 48px;
          height: 48px;
          border-radius: 6px;
          overflow: hidden;
          background: var(--bg-light);
          border: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-right: 12px;
        }

        /* MODAL DE VISTA DE IMAGEN */
        .image-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .image-modal-content {
          background: white;
          border-radius: 16px;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          animation: scaleIn 0.3s ease;
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .image-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-light);
        }

        .image-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .image-modal-actions {
          display: flex;
          gap: 8px;
        }

        .image-modal-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .download-btn {
          color: var(--primary-color);
        }

        .download-btn:hover {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .close-btn {
          color: var(--text-medium);
        }

        .close-btn:hover {
          background: var(--error-color);
          color: white;
          border-color: var(--error-color);
        }

        .image-modal-body {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: #f8f9fa;
          overflow: auto;
        }

        .modal-image-large {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        /* Resto de estilos originales del componente... */
        .daily-summary-compact {
          background: var(--bg-white);
          border-radius: 8px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          margin-bottom: 16px;
          border: 1px solid var(--border-color);
        }

        .daily-summary-grid-compact {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          padding: 12px;
        }

        .daily-metric-compact {
          padding: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border-right: 1px solid var(--border-color);
        }

        .daily-metric-compact:last-child {
          border-right: none;
        }

        .daily-metric-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .daily-metric-label {
          font-size: 11px;
          color: var(--text-medium);
          margin-bottom: 4px;
        }

        .daily-metric-trend {
          display: flex;
          align-items: center;
          gap: 2px;
          font-size: 10px;
          font-weight: 500;
          padding: 2px 4px;
          border-radius: 12px;
        }

        .daily-metric-trend.positive {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success-color);
        }

        .daily-metric-trend.negative {
          background: rgba(239, 68, 68, 0.1);
          color: var(--error-color);
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          background: var(--bg-white);
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .header-info h2 {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .header-info p {
          color: var(--text-medium);
          margin: 0;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .scan-button, .sync-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .scan-button {
          background: #0F172A;
        }

        .scan-button:hover {
          background: #1E293B;
        }

        .sync-button:hover:not(:disabled) {
          background: var(--secondary-color);
        }

        .sync-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .sync-icon.spinning {
          animation: spin 1s linear infinite;
        }

        .header-metrics {
          display: flex;
          gap: 16px;
        }

        .metric-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bg-light);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .metric-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-icon.good {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success-color);
        }

        .metric-item strong {
          display: block;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .metric-item span {
          font-size: 12px;
          color: var(--text-medium);
        }

        .sub-navigation {
          display: flex;
          gap: 4px;
          background: var(--bg-white);
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 20px;
        }

        .sub-nav-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 8px;
          background: transparent;
          border: none;
          color: var(--text-medium);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .sub-nav-item:hover {
          background: var(--bg-light);
          color: var(--text-dark);
        }

        .sub-nav-item.active {
          background: var(--primary-color);
          color: white;
        }

        .content-body {
          min-height: 400px;
        }

        .panel {
          background: var(--bg-white);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-color);
          margin-bottom: 20px;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        .half-width {
          grid-column: span 1;
        }

        .panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
        }

        .panel-header > div:first-child {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .panel-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: var(--primary-color);
          border-radius: 8px;
        }

        .panel-icon {
          width: 18px;
          height: 18px;
          color: white;
        }

        .panel-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .close-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          background: var(--bg-light);
          color: var(--text-medium);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .close-button:hover {
          background: var(--error-color);
          color: white;
          border-color: var(--error-color);
        }

        .inventory-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .category-filter {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 4px;
          max-width: 50%;
        }

        .category-button {
          padding: 4px 12px;
          border-radius: 16px;
          border: 1px solid var(--border-color);
          background: var(--bg-light);
          color: var(--text-medium);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .category-button:hover {
          background: #E5E7EB;
        }

        .category-button.active {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .products-list {
          padding: 20px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
          max-height: 600px;
          overflow-y: auto;
        }

        .product-card {
          background: var(--bg-light);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 16px;
          transition: all 0.3s ease;
        }

        .product-card:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .product-info {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .product-details {
          flex: 1;
        }

        .product-details h4 {
          margin: 0 0 4px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .category {
          color: var(--text-medium);
          font-size: 14px;
          margin: 0 0 8px;
        }

        .price {
          font-size: 18px;
          font-weight: 700;
          color: var(--primary-color);
          margin-bottom: 4px;
        }

        .barcode {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--text-medium);
          font-size: 12px;
        }

        .stock-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 8px;
          border-radius: 8px;
        }

        .stock-indicator.normal {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.2);
        }

        .stock-indicator.low {
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .stock-number {
          font-size: 20px;
          font-weight: 700;
          color: var(--success-color);
        }

        .stock-indicator.low .stock-number {
          color: var(--warning-color);
        }

        .stock-label {
          font-size: 12px;
          color: var(--text-medium);
        }

        .warning-icon {
          width: 16px;
          height: 16px;
          color: var(--warning-color);
        }

        .product-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .quantity-controls {
          display: flex;
          gap: 4px;
        }

        .qty-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          color: var(--primary-color);
        }

        .qty-btn:hover {
          background: rgba(59, 130, 246, 0.2);
        }

        .qty-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sales-list {
          padding: 20px;
          max-height: 500px;
          overflow-y: auto;
        }

        .sync-status {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .unsync-badge {
          background: rgba(245, 158, 11, 0.1);
          color: var(--warning-color);
          padding: 4px 8px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .sale-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: var(--bg-light);
          border-radius: 8px;
          margin-bottom: 8px;
          border-left: 4px solid;
        }

        .sale-item.synced {
          border-left-color: var(--success-color);
        }

        .sale-item.pending {
          border-left-color: var(--warning-color);
        }

        .sale-info {
          flex: 1;
        }

        .sale-product {
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .sale-details {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: var(--text-medium);
        }

        .amount {
          font-weight: 600;
          color: var(--primary-color);
        }

        .sync-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
        }

        .sync-indicator.synced {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success-color);
        }

        .sync-indicator.pending {
          background: rgba(245, 158, 11, 0.1);
          color: var(--warning-color);
        }

        .recommendations-list {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 500px;
          overflow-y: auto;
        }

        .recommendation-item {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: var(--bg-light);
          border-radius: 8px;
          border-left: 4px solid;
        }

        .recommendation-item.priority-high {
          border-left-color: var(--error-color);
        }

        .recommendation-item.priority-medium {
          border-left-color: var(--warning-color);
        }

        .recommendation-item.priority-low {
          border-left-color: var(--primary-color);
        }

        .rec-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: var(--bg-white);
          border-radius: 8px;
          border: 2px solid;
          flex-shrink: 0;
        }

        .rec-icon {
          width: 16px;
          height: 16px;
        }

        .rec-content {
          flex: 1;
        }

        .rec-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .rec-product {
          font-weight: 600;
          color: var(--text-dark);
        }

        .rec-priority {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .rec-priority.priority-high {
          background: rgba(239, 68, 68, 0.1);
          color: var(--error-color);
        }

        .rec-priority.priority-medium {
          background: rgba(245, 158, 11, 0.1);
          color: var(--warning-color);
        }

        .rec-priority.priority-low {
          background: rgba(59, 130, 246, 0.1);
          color: var(--primary-color);
        }

        .rec-message {
          margin: 0 0 8px;
          color: var(--text-medium);
          font-size: 14px;
          line-height: 1.4;
        }

        .rec-timestamp {
          font-size: 12px;
          color: var(--text-light);
        }

        .view-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .view-header h3 {
          font-size: 20px;
          font-weight: 600;
          color: var(--text-dark);
          margin: 0;
        }

        .add-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-button:hover {
          background: var(--secondary-color);
        }

        .clients-table, .orders-table, .sales-table {
          background: var(--bg-white);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-color);
          overflow: hidden;
        }

        .table-header {
          display: grid;
          grid-template-columns: repeat(5, 1fr) 100px;
          padding: 16px 20px;
          background: var(--bg-light);
          border-bottom: 1px solid var(--border-color);
          font-weight: 600;
          color: var(--text-dark);
        }

        .orders-table .table-header,
        .sales-table .table-header {
          grid-template-columns: 100px 2fr 1fr 1fr 1fr 100px;
        }

        .sales-table .table-header {
          grid-template-columns: 100px 1.5fr 2fr 1fr 1fr 1fr 1fr;
        }

        .table-row {
          display: grid;
          grid-template-columns: repeat(5, 1fr) 100px;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-dark);
          align-items: center;
        }

        .orders-table .table-row,
        .sales-table .table-row {
          grid-template-columns: 100px 2fr 1fr 1fr 1fr 100px;
        }

        .sales-table .table-row {
          grid-template-columns: 100px 1.5fr 2fr 1fr 1fr 1fr 1fr;
        }

        .table-row:hover {
          background: var(--bg-light);
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 1px solid var(--border-color);
          background: var(--bg-light);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.view {
          color: var(--primary-color);
        }

        .action-btn.edit {
          color: var(--warning-color);
        }

        .action-btn.delete {
          color: var(--error-color);
        }

        .action-btn:hover {
          background: var(--bg-white);
          transform: translateY(-1px);
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.pending {
          background: rgba(245, 158, 11, 0.1);
          color: var(--warning-color);
        }

        .status-badge.approved {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success-color);
        }

        .sync-status.synced {
          color: var(--success-color);
        }

        .sync-status.pending {
          color: var(--warning-color);
        }

        .barcode-scanner-view {
          max-width: 1400px;
          margin: 0 auto;
        }

        .scanner-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          padding: 20px;
        }

        .scanner-input-area {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .scanner-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .scanner-field label {
          font-weight: 600;
          color: var(--text-dark);
        }

        .barcode-input-container {
          display: flex;
          gap: 8px;
        }

        .barcode-input-container input {
          flex: 1;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          font-size: 16px;
          transition: all 0.3s ease;
        }

        .barcode-input-container input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .scan-simulate-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          background: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .scan-simulate-button:hover:not(:disabled) {
          background: var(--secondary-color);
        }

        .scan-simulate-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        .scanned-product {
          background: var(--bg-light);
          border-radius: 8px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .scanned-product-info h4 {
          margin: 0 0 8px;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .scanned-price {
          font-size: 20px;
          font-weight: 700;
          color: var(--primary-color);
        }

        .quantity-selector {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .quantity-selector label {
          font-weight: 600;
          color: var(--text-dark);
        }

        .quantity-selector .quantity-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .quantity-controls input {
          width: 60px;
          text-align: center;
          padding: 8px;
          border-radius: 6px;
          border: 1px solid var(--border-color);
          font-size: 14px;
        }

        .add-to-order-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          background: var(--success-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-to-order-button:hover {
          background: #059669;
        }

        .current-order {
          background: var(--bg-white);
          border-radius: 8px;
          border: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .current-order h3 {
          padding: 16px;
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          border-bottom: 1px solid var(--border-color);
        }

        .empty-order {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: var(--text-medium);
          gap: 16px;
          text-align: center;
        }

        .order-items {
          flex: 1;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 300px;
          overflow-y: auto;
        }

        .order-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: var(--bg-light);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .order-item-info {
          flex: 1;
        }

        .order-item-info h4 {
          margin: 0 0 4px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .order-item-details {
          display: flex;
          justify-content: space-between;
          color: var(--text-medium);
          font-size: 12px;
        }

        .order-item-total {
          font-weight: 600;
          color: var(--primary-color);
        }

        .remove-item-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.1);
          color: var(--error-color);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .remove-item-button:hover {
          background: var(--error-color);
          color: white;
        }

        .order-summary {
          padding: 16px;
          border-top: 1px solid var(--border-color);
        }

        .order-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .order-total h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }

        .order-total span {
          font-size: 20px;
          font-weight: 700;
          color: var(--primary-color);
        }

        .order-actions {
          display: flex;
          gap: 12px;
        }

        .cancel-order-button, .payment-order-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          flex: 1;
        }

        .cancel-order-button {
          background: var(--bg-light);
          color: var(--text-medium);
          border: 1px solid var(--border-color);
        }

        .cancel-order-button:hover {
          background: rgba(239, 68, 68, 0.1);
          color: var(--error-color);
          border-color: var(--error-color);
        }

        .payment-order-button {
          background: var(--success-color);
          color: white;
          border: none;
        }

        .payment-order-button:hover:not(:disabled) {
          background: #059669;
        }

        .payment-order-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Modal de pago y otros estilos mantienen su versión original */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
        }

        .modal-container {
          background: var(--bg-white);
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .payment-modal {
          max-width: 600px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
        }

        .modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .modal-close-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          background: var(--bg-light);
          color: var(--text-medium);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .modal-close-button:hover {
          background: var(--error-color);
          color: white;
          border-color: var(--error-color);
        }

        .modal-content {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .payment-summary {
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 16px;
        }

        .payment-summary h4 {
          margin: 0 0 12px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .order-summary-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
        }

        .summary-item {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 12px;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
          font-size: 14px;
        }

        .total-row {
          padding-top: 12px;
          border-top: 2px solid var(--border-color);
          font-size: 16px;
        }

        .payment-methods {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .payment-methods label {
          font-weight: 600;
          color: var(--text-dark);
        }

        .payment-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 12px;
        }

        .payment-option {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px;
          border: 2px solid var(--border-color);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .payment-option:hover {
          border-color: var(--primary-color);
          background: rgba(59, 130, 246, 0.05);
        }

        .payment-option.selected {
          border-color: var(--primary-color);
          background: rgba(59, 130, 246, 0.1);
        }

        .payment-option span {
          font-size: 12px;
          font-weight: 500;
          text-align: center;
        }

        .selected-icon {
          position: absolute;
          top: 8px;
          right: 8px;
          color: var(--primary-color);
        }

        .card-payment-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .card-visual {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .card-mockup {
          width: 300px;
          height: 190px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 20px;
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }

        .card-chip {
          width: 40px;
          height: 30px;
          background: linear-gradient(135deg, #ffd700, #ffed4a);
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .card-number {
          font-size: 20px;
          font-weight: 500;
          letter-spacing: 2px;
          margin-bottom: 20px;
        }

        .card-info {
          display: flex;
          justify-content: space-between;
          align-items: end;
        }

        .card-expiry {
          font-size: 14px;
        }

        .card-name {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .card-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-row {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-row-group {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 16px;
        }

        .form-row label {
          font-weight: 600;
          color: var(--text-dark);
          font-size: 14px;
        }

        .form-row input {
          padding: 12px;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 16px;
          transition: all 0.3s ease;
        }

        .form-row input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .security-info {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 8px;
          color: var(--success-color);
          font-size: 12px;
        }

        .client-selection {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .client-selection label {
          font-weight: 600;
          color: var(--text-dark);
        }

        .client-select {
          padding: 10px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          font-size: 14px;
          background: var(--bg-light);
        }

        .processing-payment {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          padding: 40px 20px;
          text-align: center;
        }

        .processing-animation {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .processing-payment h4 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .processing-payment p {
          margin: 0;
          color: var(--text-medium);
        }

        .processing-steps {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 20px;
        }

        .step {
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .step.completed {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success-color);
        }

        .step.active {
          background: rgba(59, 130, 246, 0.1);
          color: var(--primary-color);
        }

        .payment-success {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          padding: 40px 20px;
          text-align: center;
        }

        .success-animation {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .success-icon {
          color: var(--success-color);
          animation: successPulse 0.6s ease-in-out;
        }

        @keyframes successPulse {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }

        .payment-success h4 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--success-color);
        }

        .payment-success p {
          margin: 0;
          color: var(--text-medium);
        }

        .transaction-details {
          background: var(--bg-light);
          padding: 16px;
          border-radius: 8px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
        }

        .detail-row span:first-child {
          color: var(--text-medium);
        }

        .detail-row span:last-child {
          font-weight: 600;
          color: var(--text-dark);
        }

        .modal-footer {
          padding: 16px 20px;
          border-top: 1px solid var(--border-color);
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .cancel-button, .back-button {
          padding: 10px 16px;
          border-radius: 8px;
          background: var(--bg-light);
          color: var(--text-medium);
          border: 1px solid var(--border-color);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-button:hover, .back-button:hover {
          background: rgba(239, 68, 68, 0.1);
          color: var(--error-color);
          border-color: var(--error-color);
        }

        .continue-button, .pay-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .continue-button:hover, .pay-button:hover {
          background: var(--secondary-color);
        }

        .pay-button {
          background: var(--success-color);
        }

        .pay-button:hover {
          background: #059669;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .inventory-grid {
            grid-template-columns: 1fr;
          }
          
          .scanner-content {
            grid-template-columns: 1fr;
          }

          .daily-summary-grid-compact {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 1024px) {
          .products-list {
            grid-template-columns: 1fr;
          }

          .payment-options {
            grid-template-columns: 1fr;
          }

          .form-row-group {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .content-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .header-actions {
            justify-content: space-between;
          }

          .daily-summary-grid-compact {
            grid-template-columns: repeat(2, 1fr);
          }

          .table-header, .table-row {
            grid-template-columns: repeat(3, 1fr);
          }

          .sub-navigation {
            overflow-x: auto;
          }

          .card-mockup {
            width: 260px;
            height: 160px;
          }

          .image-modal-content {
            max-width: 95vw;
            max-height: 95vh;
          }

          .modal-image-large {
            max-height: 60vh;
          }
        }

        @media (max-width: 576px) {
          .daily-summary-grid-compact {
            grid-template-columns: 1fr;
          }

          .table-header, .table-row {
            grid-template-columns: 1fr 1fr;
          }

          .order-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .remove-item-button {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
};

export default PointOfSale;